import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { Entity } from 'shared-libs/models/entity'
import { Settings } from 'browser/app/models/settings'
import { AutofillBlock } from 'browser/components/atomic-elements/atoms/autofill-block/autofill-block'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { CardHeader } from 'browser/components/atomic-elements/atoms/card/card-header'
import { CardHeaderItem } from 'browser/components/atomic-elements/atoms/card/card-header-item'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { Section } from 'browser/components/atomic-elements/atoms/section/section'
import { EntityDataSource } from 'browser/components/atomic-elements/organisms/entity/entity-data-source'
import { EntityTable } from 'browser/components/atomic-elements/organisms/entity/entity-table'

const DEFAULT_ORDERS = [
  {
    path: 'precomputation.displayName',
    type: 'ascending',
  },
]
const CARRIER_TYPE = '/1.0/entities/metadata/carrier.json'
const INTEGRATION_ENTITY_TYPE = '/1.0/entities/metadata/integrationEntities.json'
const QB_SYNC_TYPE = '/1.0/entities/metadata/quickBooksSync.json'

interface ICarrierQBOMappingProps extends IBaseProps {
  settings: Settings
}

export class CarrierQBOMapping extends React.Component<ICarrierQBOMappingProps, any> {

  private carrierDataSet: EntityDataSource
  private integrationDataSet: EntityDataSource
  private carrierSchema: any
  private qbSyncSchema: any

  constructor(props: ICarrierQBOMappingProps) {
    super(props)
    const store = apis.getStore()

    this.carrierSchema = store.getRecord(CARRIER_TYPE)
    this.qbSyncSchema = store.getRecord(QB_SYNC_TYPE)

    this.carrierDataSet = new EntityDataSource({
      entityType: CARRIER_TYPE,
      orders: DEFAULT_ORDERS,
    }).setOnChange(() => this.forceUpdate())

    this.integrationDataSet = new EntityDataSource({
      entityType: INTEGRATION_ENTITY_TYPE,
      orders: DEFAULT_ORDERS,
    }).setOnChange(this.handleIntegrationDataChange)

    this.state = {
      isTableScrollEnabled: true,
      options: [],
    }
  }

  public componentDidMount() {
    this.carrierDataSet.find()
    this.integrationDataSet.find()
  }

  public componentWillUnmount() {
    this.carrierDataSet.dispose()
    this.integrationDataSet.dispose()
  }

  public render() {
    return (
      <div className='grid-block vertical'>
        <CardHeader className='c-cardHeader--nonSeparating'>
          <CardHeaderItem
            className='c-cardHEader-item--grow c-cardHeader-item--center'
            title='Carrier Mappings'
          />
          {/* {this.renderAutoMapButton()} */}
        </CardHeader>
        <div className='grid-block'>
          {this.renderMappingTable()}
        </div>
      </div>
    )
  }

  public renderAutoMapButton() {
    return (
      <CardHeaderItem>
        <Button
          className='c-button--secondary'
          onClick={this.handleSuggestedMappings}
        >
          Auto-Map
        </Button>
      </CardHeaderItem>
    )
  }

  private handleIntegrationDataChange = () => {
    const { entities } = this.integrationDataSet
    if (_.isEmpty(entities)) {
      return
    }
    entities[0].reload().then((entity: Entity) => {
      const options = _.filter(entity.content.integrationEntities.entities, (a) => {
        return a.entityType === 'Carrier'
      })
      this.setState({
        options,
      })
    })
  }

  private handleSuggestedMappings = () => {
    const { integrationDataSet } = this
    // TODO(david)
    // const uniqueId = _.get(integrationDataSet.entities[0], 'uniqueId', '')
    // apis.quickbooksAutoMapping(
    //   settings.getFirm().uniqueId,
    //   'Carrier',
    //   uniqueId,
    //   true,
    // )
  }

  private renderMappingTable() {
    const { carrierDataSet, carrierSchema } = this
    if (!this.isQBOInstalled()) {
      return this.renderConnectToQuickbooks()
    }

    let content
    if (this.integrationDataSet.isLoading || this.carrierDataSet.isLoading) {
      content = (
        <LoadingSpinner />
      )
    } else {
      content = (
        <EntityTable
          columns={this.renderTableColumns()}
          entitySchema={carrierSchema}
          emptyState={this.renderEmptyState()}
          isLoading={carrierDataSet.isLoading}
          onEndReached={carrierDataSet.handleLoadNextPage}
          onVerticalScroll={this.handleOnVerticalScroll}
          rows={carrierDataSet.content}
        />
      )
    }

    return (
      <Section
        className='grid-block vertical u-bumperLeft--lg u-bumperRight--lg u-bumperBottom--lg c-section--collapse'
        headerClassName='grid-block shrink'
        bodyClassName='grid-block'
      >
        <AutofillBlock className='u-border'>
          {content}
        </AutofillBlock>
      </Section>
    )
  }

  private handleOnVerticalScroll = () => {
    const { isTableScrollEnabled } = this.state
    return isTableScrollEnabled
  }

  private renderTableColumns() {
    const { options } = this.state

    return [
      {
        fixed: true,
        flexGrow: 1,
        isSortable: true,
        label: 'Carrier Name',
        path: 'displayName',
        width: 170,
      },
      {
        cellComponent: {
          onChange: this.handleQBOEntitySelected,
          onSelectMenuChange: this.handleSelectMenuChange,
          optionLabelPath: 'name',
          optionValuePath: 'identifier',
          options,
          placeholder: 'QBO Carriers',
          size: 'sm',
          type: 'ui:table:select',
        },
        label: 'QBO Carrier Mapping',
        path: 'displayName',
        width: 400,
      },
      {
        cellComponent: {
          businessType: 'Carrier',
          className: 'u-flexGrow tr',
          dataSet: this.integrationDataSet,
          size: 'xs',
          type: 'ui:table:accountingMappingCell',
        },
        flexGrow: 1,
        label: 'Mapping Options',
        path: '',
        width: 300,
      },
    ]
  }

  private handleSelectMenuChange = () => {
    const { isTableScrollEnabled } = this.state
    this.setState({
      isTableScrollEnabled: !isTableScrollEnabled,
    })
  }

  private handleQBOEntitySelected = (value: unknown, option: { identifier: string; version: string }, entity: Entity) => {
    this.addQuickBooksSync(entity, option)
  }

  private addQuickBooksSync(entity: Entity, qbs: { identifier: string; version: string }) {
    const activeMixins = entity.activeMixins
    if (!_.find(activeMixins, (mixin) => mixin.entityId === '164fd38f-5ea0-41f5-bf35-5aae09ddcb7d')) {
      entity.addMixin(this.qbSyncSchema)
    }
    entity.set('quickBooksSync.identifier', qbs.identifier)
    entity.set('quickBooksSync.version', qbs.version)
    entity.save()
  }

  private renderEmptyState() {
    return (
      <div className='grid-block align-verticalCenter u-justifyContentCenter'>
        <HelpBlock>
          No Carriers Found
        </HelpBlock>
      </div>
    )
  }

  private renderConnectToQuickbooks() {
    return (
      <div className='grid-block align-verticalCenter u-justifyContentCenter'>
        <HelpBlock>
          Connect to Quickbooks to map customers.
        </HelpBlock>
      </div>
    )
  }

  private isQBOInstalled() {
    const { settings } = this.props
    const qboSettings = settings.getQuickBooksOnlineSettings()
    return qboSettings.get('settings.isInstalled', false)
  }
}
